import { useEffect, useState, useCallback } from 'react';
import { Calendar, Users, ChevronRight, Compass, Check, X, ArrowLeftRight, StickyNote, Mic } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useTutorial } from '../contexts/TutorialContext';
import { format, isAfter, parseISO, isToday, isTomorrow, differenceInDays, startOfDay } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { Assignment, Team, TeamMember } from '../lib/database.types';
import { CardSkeleton } from '../components/ui/Skeleton';
import OnboardingFlow from '../components/onboarding/OnboardingFlow';

interface UpcomingAssignment extends Assignment {
  shift: {
    id: string;
    name: string;
    start_time: string;
    end_time: string;
    notes: string | null;
    team: Team;
    service: {
      id: string;
      name: string;
      date: string;
      notes: string | null;
      voorganger: string | null;
    };
  };
}

const ONBOARDING_DISMISSED_KEY = 'kerkplanner_onboarding_dismissed';

function getDateLabel(dateStr: string): string {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'Vandaag';
  if (isTomorrow(date)) return 'Morgen';
  const days = differenceInDays(date, startOfDay(new Date()));
  if (days <= 7) return format(date, 'EEEE', { locale: nl });
  return format(date, 'EEEE d MMMM', { locale: nl });
}

function getUrgencyClass(dateStr: string): string {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-900/10';
  if (isTomorrow(date)) return 'border-l-amber-500 bg-amber-50/30 dark:bg-amber-900/10';
  return 'border-l-slate-300 dark:border-l-slate-600 bg-white dark:bg-slate-800';
}

export default function Dashboard() {
  const { profile } = useAuth();
  const { startTour, tourCompleted } = useTutorial();
  const [upcomingAssignments, setUpcomingAssignments] = useState<UpcomingAssignment[]>([]);
  const [myTeams, setMyTeams] = useState<TeamMember[]>([]);
  const [stats, setStats] = useState({ upcomingCount: 0, pendingSwaps: 0, myTeamsCount: 0 });
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const dismissOnboarding = useCallback(() => {
    setShowOnboarding(false);
    localStorage.setItem(ONBOARDING_DISMISSED_KEY, 'true');
  }, []);

  useEffect(() => {
    if (profile?.id) {
      fetchData();
    }
  }, [profile?.id]);

  async function fetchData() {
    setLoading(true);

    const [assignmentsResult, teamsResult, swapsPendingResult] = await Promise.all([
      supabase
        .from('assignments')
        .select(`
          *,
          shift:shifts(
            id,
            name,
            start_time,
            end_time,
            notes,
            team:teams(*),
            service:services(id, name, date, notes, voorganger)
          )
        `)
        .eq('user_id', profile?.id)
        .neq('status', 'cancelled'),
      supabase
        .from('team_members')
        .select('*, team:teams(*, congregation:congregations(id, short_name, color))')
        .eq('user_id', profile?.id)
        .eq('is_active', true),
      supabase
        .from('swap_requests')
        .select('id')
        .eq('requested_by', profile?.id)
        .eq('status', 'pending'),
    ]);

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (assignmentsResult.data) {
      const upcoming = (assignmentsResult.data as UpcomingAssignment[])
        .filter((a) => {
          const serviceDate = a.shift?.service?.date;
          return serviceDate && isAfter(parseISO(serviceDate), now);
        })
        .sort((a, b) => {
          const dateA = a.shift?.service?.date || '';
          const dateB = b.shift?.service?.date || '';
          return dateA.localeCompare(dateB);
        });
      setUpcomingAssignments(upcoming);
      setStats(prev => ({ ...prev, upcomingCount: upcoming.length }));
    }

    if (teamsResult.data) {
      setMyTeams(teamsResult.data);
      setStats(prev => ({ ...prev, myTeamsCount: teamsResult.data.length }));
    }

    setStats(prev => ({ ...prev, pendingSwaps: swapsPendingResult.data?.length || 0 }));

    const dismissed = localStorage.getItem(ONBOARDING_DISMISSED_KEY);
    if (!dismissed && (teamsResult.data?.length || 0) === 0 && !assignmentsResult.data?.length) {
      setShowOnboarding(true);
    }

    setLoading(false);
  }

  async function updateStatus(assignmentId: string, status: 'confirmed' | 'declined') {
    const { error } = await supabase
      .from('assignments')
      .update({ status })
      .eq('id', assignmentId);

    if (!error) {
      setUpcomingAssignments(prev =>
        prev.map(a => a.id === assignmentId ? { ...a, status } : a)
      );
    }
  }

  const nextAssignments = upcomingAssignments.slice(0, 3);
  const remainingAssignments = upcomingAssignments.slice(3, 8);
  const isAdmin = profile?.role === 'superadmin' || profile?.role === 'beheerder' || profile?.role === 'groepsbeheerder';

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Dashboard</h1>
          <p className="text-slate-600 dark:text-slate-400">Overzicht van je planning en activiteiten</p>
        </div>
        <div className="space-y-3">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Mijn rooster</h1>
        <p className="text-slate-600 dark:text-slate-400">
          {nextAssignments.length > 0
            ? 'Je eerstvolgende diensten'
            : 'Overzicht van je planning en activiteiten'}
        </p>
      </div>

      {showOnboarding && (
        <div className="space-y-4">
          <OnboardingFlow onDismiss={dismissOnboarding} />
          {!tourCompleted && (
            <button
              onClick={() => {
                dismissOnboarding();
                startTour();
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-medium rounded-xl hover:from-amber-700 hover:to-amber-600 transition-all shadow-lg"
            >
              <Compass className="w-5 h-5" />
              Of neem een rondleiding door de app
            </button>
          )}
        </div>
      )}

      {nextAssignments.length > 0 ? (
        <div className="space-y-3">
          {nextAssignments.map((assignment, index) => {
            const serviceDate = assignment.shift?.service?.date || '';
            const dateLabel = getDateLabel(serviceDate);
            const urgency = getUrgencyClass(serviceDate);
            const isFirst = index === 0;

            return (
              <div
                key={assignment.id}
                className={`rounded-xl border border-slate-200 dark:border-slate-700 border-l-4 overflow-hidden transition-all ${urgency} ${
                  isFirst ? 'shadow-sm' : ''
                }`}
              >
                <div className={`p-4 ${isFirst ? 'sm:p-5' : 'sm:p-4'}`}>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div
                      className={`${isFirst ? 'w-12 h-12 sm:w-14 sm:h-14' : 'w-10 h-10 sm:w-12 sm:h-12'} rounded-xl flex flex-col items-center justify-center flex-shrink-0 text-white`}
                      style={{ backgroundColor: assignment.shift?.team?.color || '#84D2C5' }}
                    >
                      <span className={`${isFirst ? 'text-lg sm:text-xl' : 'text-base sm:text-lg'} font-bold leading-none`}>
                        {serviceDate && format(parseISO(serviceDate), 'd')}
                      </span>
                      <span className={`${isFirst ? 'text-[10px] sm:text-xs' : 'text-[9px] sm:text-[10px]'} uppercase leading-none mt-0.5 opacity-90`}>
                        {serviceDate && format(parseISO(serviceDate), 'MMM', { locale: nl })}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`${isFirst ? 'text-xs sm:text-sm' : 'text-xs'} font-semibold ${
                              isToday(parseISO(serviceDate)) ? 'text-amber-600 dark:text-amber-400' :
                              isTomorrow(parseISO(serviceDate)) ? 'text-amber-600 dark:text-amber-400' :
                              'text-slate-500 dark:text-slate-400'
                            }`}>
                              {dateLabel}
                            </span>
                            <span className={`${isFirst ? 'text-xs sm:text-sm' : 'text-xs'} text-slate-400 dark:text-slate-500`}>
                              {assignment.shift?.start_time?.slice(0, 5)} - {assignment.shift?.end_time?.slice(0, 5)}
                            </span>
                          </div>
                          <h3 className={`${isFirst ? 'text-base sm:text-lg' : 'text-sm sm:text-base'} font-semibold text-slate-800 dark:text-slate-100 truncate`}>
                            {assignment.shift?.name}
                          </h3>
                          <p className={`${isFirst ? 'text-sm' : 'text-xs sm:text-sm'} text-slate-500 dark:text-slate-400 truncate`}>
                            {assignment.shift?.team?.name}
                            {assignment.shift?.service?.name && ` \u2014 ${assignment.shift.service.name}`}
                          </p>
                        </div>

                        <span
                          className={`flex-shrink-0 px-2 py-1 text-xs font-medium rounded-full ${
                            assignment.status === 'confirmed'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'
                              : assignment.status === 'declined'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                          }`}
                        >
                          {assignment.status === 'confirmed' ? 'Bevestigd' : assignment.status === 'declined' ? 'Afgewezen' : 'Toegewezen'}
                        </span>
                      </div>

                      {(assignment.shift?.service?.voorganger || assignment.shift?.service?.notes || assignment.shift?.notes) && (
                        <div className="mt-2 space-y-1">
                          {assignment.shift?.service?.voorganger && (
                            <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                              <Mic className="w-3 h-3 flex-shrink-0" />
                              <span>{assignment.shift.service.voorganger}</span>
                            </div>
                          )}
                          {assignment.shift?.service?.notes && (
                            <div className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                              <StickyNote className="w-3 h-3 flex-shrink-0 mt-0.5" />
                              <span className="italic line-clamp-2">{assignment.shift.service.notes}</span>
                            </div>
                          )}
                          {assignment.shift?.notes && (
                            <div className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
                              <StickyNote className="w-3 h-3 flex-shrink-0 mt-0.5" />
                              <span className="line-clamp-2">{assignment.shift.notes}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {assignment.status === 'assigned' && (
                        <div className="flex items-center gap-2 mt-3">
                          <button
                            onClick={() => updateStatus(assignment.id, 'confirmed')}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                          >
                            <Check className="w-4 h-4" />
                            Bevestigen
                          </button>
                          <button
                            onClick={() => updateStatus(assignment.id, 'declined')}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                          >
                            <X className="w-4 h-4" />
                            Afwijzen
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {remainingAssignments.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {remainingAssignments.map((assignment) => {
                  const serviceDate = assignment.shift?.service?.date || '';
                  return (
                    <div key={assignment.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: assignment.shift?.team?.color || '#84D2C5' }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                            {assignment.shift?.name}
                          </span>
                          <span className="text-xs text-slate-400 dark:text-slate-500 flex-shrink-0">
                            {assignment.shift?.team?.name}
                          </span>
                        </div>
                      </div>
                      <span className="text-xs text-slate-500 dark:text-slate-400 flex-shrink-0">
                        {serviceDate && format(parseISO(serviceDate), 'EEE d MMM', { locale: nl })}
                      </span>
                      <span
                        className={`flex-shrink-0 w-2 h-2 rounded-full ${
                          assignment.status === 'confirmed' ? 'bg-emerald-500' :
                          assignment.status === 'declined' ? 'bg-red-500' :
                          'bg-amber-500'
                        }`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {upcomingAssignments.length > 8 && (
            <Link
              to="/mijn-rooster"
              className="flex items-center justify-center gap-2 py-3 text-sm font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
            >
              Bekijk alle {upcomingAssignments.length} diensten
              <ChevronRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 sm:p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-slate-400 dark:text-slate-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">Geen komende diensten</h3>
          <p className="text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
            Zodra een coordinator je inplant verschijnen je diensten hier
          </p>
          <Link
            to="/mijn-rooster"
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 text-sm font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
          >
            Bekijk je volledige rooster <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Link
          to="/mijn-rooster"
          className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700 card-hover flex items-center gap-3"
        >
          <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400">Totaal komend</p>
            <p className="text-lg font-bold text-slate-800 dark:text-slate-100">{stats.upcomingCount}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600 flex-shrink-0" />
        </Link>

        <Link
          to="/ruilverzoeken"
          className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700 card-hover flex items-center gap-3"
        >
          <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
            <ArrowLeftRight className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400">Ruilverzoeken</p>
            <p className="text-lg font-bold text-slate-800 dark:text-slate-100">{stats.pendingSwaps}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600 flex-shrink-0" />
        </Link>

        <Link
          to={isAdmin ? '/teams' : '/mijn-rooster'}
          className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700 card-hover flex items-center gap-3"
        >
          <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400">Mijn teams</p>
            <p className="text-lg font-bold text-slate-800 dark:text-slate-100">{stats.myTeamsCount}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600 flex-shrink-0" />
        </Link>
      </div>

      {myTeams.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between p-4 border-b border-slate-100 dark:border-slate-700">
            <h2 className="font-semibold text-slate-800 dark:text-slate-200">Mijn teams</h2>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {myTeams.map((membership) => {
              const cong = (membership.team as any)?.congregation;
              return (
                <div key={membership.id} className="p-3 sm:p-4 flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: membership.team?.color || '#84D2C5' }}
                  >
                    <Users className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{membership.team?.name}</p>
                      {cong?.short_name && (
                        <span
                          className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded"
                          style={{ backgroundColor: cong.color + '20', color: cong.color }}
                        >
                          {cong.short_name}
                        </span>
                      )}
                    </div>
                  </div>
                  {membership.role === 'coordinator' && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 rounded-full whitespace-nowrap">
                      Coordinator
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
